<template>
  <app-page :title="$t('@customer/pageTitle')">
    <errors-list :errorMessage="errorMessage" />

    <v-dialog v-model="modal" persistent lazy full-width width="640px">
      <customer-form :customer="customer" @close-modal="onEdited" v-if="modal" />
    </v-dialog>

    <v-layout>
      <v-flex xs6>
        <v-list dense>
          <v-subheader>{{ $t('@customer/personalInfo/title') }}</v-subheader>

          <v-list-tile>
            <v-list-tile-content>
              {{ $t('@customer/personalInfo/fullNameLabel') }}
            </v-list-tile-content>
            <v-list-tile-content class="align-end">
              {{ customer.firstName }} {{ customer.lastName }}
            </v-list-tile-content>
          </v-list-tile>

          <v-list-tile>
            <v-list-tile-content>
              {{ $t('@customer/personalInfo/personalNumberLabel') }}
            </v-list-tile-content>
            <v-list-tile-content class="align-end">
              {{ customer.personalNumber }}
            </v-list-tile-content>
          </v-list-tile>

          <v-list-tile>
            <v-list-tile-content>
              {{ $t('@customer/personalInfo/since') }}
            </v-list-tile-content>
            <v-list-tile-content class="align-end">
              {{ customer.created | defaultDateTimeFormatter }}
            </v-list-tile-content>
          </v-list-tile>
        </v-list>
      </v-flex>

      <v-flex xs6>
        <v-list dense>
          <v-subheader>{{ $t('@customer/contacts/title') }}</v-subheader>

          <v-list-tile>
            <v-list-tile-content>
              {{ $t('@customer/contacts/emailLabel') }}
            </v-list-tile-content>
            <v-list-tile-content class="align-end">{{ customer.email }}</v-list-tile-content>
          </v-list-tile>

          <v-list-tile>
            <v-list-tile-content>
              {{ $t('@customer/contacts/mobilePhoneLabel') }}
            </v-list-tile-content>
            <v-list-tile-content class="align-end">{{ customer.mobilePhone }}</v-list-tile-content>
          </v-list-tile>

          <v-list-tile v-if="customer.address">
            <v-list-tile-content>
              {{ $t('@customer/contacts/addressLabel') }}
            </v-list-tile-content>
            <v-list-tile-content class="align-end">
              {{ customer.address }}, {{ customer.city }}
            </v-list-tile-content>
          </v-list-tile>

          <v-list-tile v-if="customer.address">
            <v-list-tile-content>
              {{ $t('@customer/contacts/addressUpdatedLabel') }}
            </v-list-tile-content>
            <v-list-tile-content class="align-end">
              {{ customer.addressUpdated | defaultDateTimeFormatter }}
            </v-list-tile-content>
          </v-list-tile>
        </v-list>
      </v-flex>
    </v-layout>

    <v-layout row wrap align-content-start>
      <v-flex xs12>
        <v-card class="mb-3" flat>
          <v-card-title>
            <v-icon class="mr-2">assignment</v-icon>
            <h4>{{ $t('@customer/insurancesListTitle') }}</h4>
          </v-card-title>
          <v-divider />

          <contract-list
            :filters="query"
            :headers="headers"
            @selected="onContractSelected"
          ></contract-list>
        </v-card>
      </v-flex>

      <v-flex xs12>
        <v-card class="mb-3" flat>
          <v-card-title>
            <v-icon class="mr-2">history</v-icon>
            <h4>{{ $t('@customer/auditTrailEventsListTitle') }}</h4>
          </v-card-title>
          <v-divider />

          <audit-trail-list
            v-if="customer.id"
            :filters="{ customerId: customer.id, from: customer.created }"
          />
        </v-card>
      </v-flex>
    </v-layout>

    <v-speed-dial
      v-if="!isCompanyAgent"
      :value="true"
      top
      right
      absolute
      style="top: 64px; right: -52px;"
      direction="bottom"
      transition="slide-y-reverse-transition"
    >
      <v-tooltip right :value="true">
        <template v-slot:activator="{ on }">
          <v-btn fab dark small color="orange" @click.stop="modal = true">
            <v-icon>create</v-icon>
          </v-btn>
        </template>
        <span>
          {{ $t('@customer/edit') }}
        </span>
      </v-tooltip>

      <v-tooltip right :value="true">
        <template v-slot:activator="{ on }">
          <v-btn fab dark small color="green" @click.stop="manaulUcCheck">
            <v-icon>cached</v-icon>
          </v-btn>
        </template>
        <span>
          {{ $t('@customer/manualUcCheck') }}
        </span>
      </v-tooltip>
    </v-speed-dial>
  </app-page>
</template>

<script>
import ErrorsList from '../../components/ErrorsList';
import ContractList, { contractStatusFieldMap, defaultHeaders } from '../Contracts/ContractList';
import AuditTrailList from './AuditTrailList';
import CustomerForm from './CustomerForm';

import { getErrorMessage } from '../../errorHandlers';
import { defaultDateTimeFormatter } from '../../utils';

import { mapGetters } from 'vuex';
import { CUSTOMER_UPDATES_EMAIL, CUSTOMER_UPDATES_PHONE_NUMBER } from '../../wsEventTypes';

export default {
  components: { ContractList, AuditTrailList, CustomerForm, ErrorsList },

  data() {
    return {
      errorMessage: '',
      loading: false,
      query: {
        customerId: null,
      },
      customer: {
        id: 0,
        firstName: '',
        lastName: '',
        personalNumber: '',
      },
      headers: [
        ...defaultHeaders.filter((h) => h.value !== 'agent.fullName'),
        {
          text: 'Status',
          align: 'left',
          sortable: true,
          value: 'status',
          format: (s) => this.$t(contractStatusFieldMap[s]),
        },
      ],
      modal: false,
      ucLoading: false,
    };
  },

  watch: {
    query() {
      this.fetchProfile();
    },
  },

  computed: {
    ...mapGetters(['isCompanyAgent']),
  },

  methods: {
    async fetchProfile(force = false) {
      if ((Number(this.query.customerId) === Number(this.customer.id) && !force) || this.loading) {
        return;
      }
      try {
        this.loading = true;
        const profile = await this.$http.get(`/api/customer/${this.query.customerId}`);
        this.customer = profile.data;
        this.errorMessage = '';
      } catch (e) {
        this.errorMessage = getErrorMessage(e);
      } finally {
        this.loading = false;
      }
    },

    onContractSelected(contract) {
      this.$router.push(`/contract/${contract.internalId}`);
    },

    onEdited(changed) {
      if (changed) {
        this.fetchProfile(true);
      }
      this.modal = false;
    },

    setQueryFromRoute(route) {
      this.query = {
        ...this.query,
        ...route.query,
        customerId: route.params.customerId,
      };
    },

    async manaulUcCheck() {
      try {
        this.ucLoading = true;
        await this.$http.post(`/api/customer/${this.query.customerId}/uc`);
        this.errorMessage = '';
        await this.fetchProfile(true);
      } catch (e) {
        if (e.response && e.response.status === 429) {
          this.errorMessage = this.$t('@customer/hitRateLimit');
          return;
        }
        this.errorMessage = getErrorMessage(e);
      } finally {
        this.ucLoading = false;
      }
    },
  },

  filters: {
    defaultDateTimeFormatter,
  },

  sockets: {
    insuranceSubscribe: function(data) {
      const eventTypes = [CUSTOMER_UPDATES_EMAIL, CUSTOMER_UPDATES_PHONE_NUMBER];

      if (data.meta && data.meta.customerId === Number(this.query.customerId)) {
        if (eventTypes.includes(data.type)) {
          this.fetchProfile();
        }
      }
    },
  },

  mounted() {
    this.setQueryFromRoute(this.$router.currentRoute);
  },

  beforeRouteUpdate(to, from, next) {
    this.setQueryFromRoute(to);
    next();
  },
};
</script>
<style scoped>
.v-list__tile__content.align-end {
  align-items: flex-end;
}
</style>
